import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import NewsCard from '@/components/base/NewsCard';
import { newsList } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = newsList.find((news) => news.id === id);
  const [copied, setCopied] = useState(false);

  const related = useMemo(
    () => newsList.filter((news) => news.id !== id && news.category === item?.category).slice(0, 3),
    [id, item],
  );

  usePageMeta({
    title: item ? `${item.title} - 草民电影` : '资讯详情 - 草民电影',
    description: item ? item.summary.slice(0, 100) : '草民电影资讯详情页。',
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!item) {
    return (
      <div className="w-full px-4 py-16 md:px-6">
        <EmptyState
          title="该资讯信息暂缺"
          message="你访问的资讯可能已下架或链接有误，建议查看其他资讯。"
          icon="ri-file-unknow-line"
          actionLabel="浏览全部资讯"
          onAction={() => window.REACT_APP_NAVIGATE('/news')}
        />
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '电影资讯', to: '/news' },
          { label: item.category },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
        <article className="min-w-0">
          <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
            {item.category}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {item.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" /> {item.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line" /> 草民电影编辑部
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 whitespace-nowrap text-primary-700 transition-colors hover:text-accent-600"
            >
              <i className={copied ? 'ri-check-line' : 'ri-link'} />
              {copied ? '已复制链接' : '复制链接'}
            </button>
          </div>

          <div className="mt-6 w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={`${item.title} 电影资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {item.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 rounded-lg bg-secondary-50 p-5 text-sm leading-relaxed text-secondary-900">
            <i className="ri-information-line mr-1" />
            本站内容为演示性质，不提供任何影片播放与下载服务。
          </div>
        </article>

        <aside className="flex flex-col gap-4">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="inline-block h-5 w-1 rounded-full bg-accent-500" />
            相关资讯
          </h2>
          {related.length === 0 ? (
            <p className="rounded-lg border border-dashed border-background-300 bg-background-100 p-6 text-sm text-foreground-500">
              暂无相关内容，敬请期待
            </p>
          ) : (
            related.map((news) => <NewsCard key={news.id} item={news} />)
          )}
          <Link
            to="/news"
            className="flex items-center justify-center gap-1 rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            返回资讯列表 <i className="ri-arrow-right-line" />
          </Link>
        </aside>
      </div>
    </div>
  );
}